import type { ParseKeys } from 'i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Search } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { LibrarySearch } from '@/components/library/LibrarySearch'
import { LibraryToolbar } from '@/components/library/LibraryToolbar'
import { Text } from '@/components/ui/Text'
import { connectedSourcesApi } from '@/lib/api'
import type { ConnectedSourceConnector } from '@/lib/api'
import type { IntegrationRequestState } from '@/types/components/connectors/marketplace'
import { FacetFilterPill } from './KnowledgeLibraryBand'
import { knowledgeFacetKindLabel } from './libraryHelpers'
import { IntegrationTile } from './IntegrationTile'
import { IntegrationDetails } from './IntegrationDetails'
import { integrationSummaryKey, orderSources } from './librarySourceCatalog'
import { useLibrarySources } from './useLibrarySources'

const INTEGRATIONS_PAGE_SIZE = 24

export function LibrarySources() {
  const { t } = useTranslation('app')
  const { connectors, loading, unavailable, busy, actionError, load, connect, disconnect } = useLibrarySources()
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [visibleCount, setVisibleCount] = useState(INTEGRATIONS_PAGE_SIZE)
  const moreRef = useRef<HTMLDivElement>(null)
  const changeSearch = (value: string) => {
    setSearch(value)
    setVisibleCount(INTEGRATIONS_PAGE_SIZE)
  }
  const changeCategory = (value: string) => {
    setCategory(value)
    setVisibleCount(INTEGRATIONS_PAGE_SIZE)
  }
  const [selectedSlug, setSelectedSlug] = useState('')
  const [requestStates, setRequestStates] = useState<Record<string, IntegrationRequestState>>({})
  const setRequestState = (slug: string, state: IntegrationRequestState) => setRequestStates(previous => ({ ...previous, [slug]: state }))
  const [requested, setRequested] = useState<Set<string>>(new Set())
  const requesting = useRef(new Set<string>())
  const openerRef = useRef<HTMLElement | null>(null)
  const rememberOpener = () => { openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null }
  const selected = connectors.find(item => item.slug === selectedSlug) ?? null
  const categories = [...new Set(connectors.flatMap(item => item.categories ?? []))].sort()
  const query = search.trim().toLocaleLowerCase()
  const filtered = useMemo(() => orderSources(connectors).filter(item => (!category || item.categories.includes(category)) &&
    (!query || [item.name, t(integrationSummaryKey(item.slug), { name: item.name }), ...item.categories].join(' ').toLocaleLowerCase().includes(query))), [connectors, category, query, t])
  const active = filtered.filter(item => item.authenticated)
  const inactive = filtered.filter(item => !item.authenticated)
  const hasMore = inactive.length > visibleCount
  const loadMore = useCallback(() => {
    setVisibleCount(count => count + INTEGRATIONS_PAGE_SIZE)
  }, [])

  useEffect(() => {
    const node = moreRef.current
    if (!node || !hasMore || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        observer.disconnect()
        loadMore()
      }
    }, { rootMargin: '0px 0px 400px 0px' })
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, loadMore, visibleCount])
  const details = (item: ConnectedSourceConnector) => {
    rememberOpener()
    setSelectedSlug(item.slug)
    setRequestState(item.slug, requested.has(item.slug) ? 'sent' : requesting.current.has(item.slug) ? 'sending' : 'idle')
  }
  const request = async (item: ConnectedSourceConnector) => {
    if (requesting.current.has(item.slug)) return
    if (requested.has(item.slug)) { details(item); return }
    rememberOpener()
    requesting.current.add(item.slug)
    setSelectedSlug(item.slug)
    setRequestState(item.slug, 'sending')
    try {
      await connectedSourcesApi.request(item.slug)
      setRequested(previous => new Set(previous).add(item.slug))
      setRequestState(item.slug, 'sent')
    } catch { setRequestState(item.slug, 'error') }
    finally { requesting.current.delete(item.slug) }
  }
  const section = (items: ConnectedSourceConnector[], title: ParseKeys<'app'>, description: ParseKeys<'app'>, total = items.length) => items.length > 0 && (
    <section className="flex flex-col gap-4">
      <div className="px-3"><Text className="text-lg font-medium">{t(title)} <span className="ml-1 text-sm font-normal text-muted-foreground">{total}</span></Text><Text size="sm" color="muted" className="mt-1">{t(description)}</Text></div>
      <div className="grid grid-cols-1 gap-x-12 gap-y-1 md:grid-cols-2">
        {items.map(item => <IntegrationTile key={item.slug} connector={item} busy={busy === item.slug} requested={requested.has(item.slug)}
          onDetails={() => details(item)} onConnect={() => { void connect(item.slug) }} onRequest={() => { void request(item) }} />)}
      </div>
    </section>
  )
  return (
    <section className="flex w-full flex-col gap-6" data-testid="library-sources">
      <LibraryToolbar
        filters={<FacetFilterPill
          kind="category"
          label={knowledgeFacetKindLabel('category')}
          agg={{ kind: 'category', displayName: knowledgeFacetKindLabel('category'), values: categories.map(value => ({ value, count: connectors.filter(item => item.categories.includes(value)).length })), unknownCount: 0 }}
          value={category || null}
          onChange={value => changeCategory(value ?? '')}
          allLabel={t('connectors.allCategories')}
        />}
        search={<LibrarySearch compact value={search} onChange={changeSearch} placeholder={t('connectors.searchPlaceholder')} clearLabel={t('connectors.clearSearch')} />}
      />
      {(unavailable || actionError) && <div role="alert" className="flex items-center justify-between gap-4 rounded-xl border border-border px-4 py-3"><Text size="sm" color="muted">{t('connectors.unavailable')}</Text><Button variant="ghost" size="sm" onClick={() => { void load() }}>{t('connectors.retry')}</Button></div>}
      {loading && <div className="grid grid-cols-1 gap-6 px-3 md:grid-cols-2" aria-label={t('connectors.loading')}>{Array.from({ length: 6 }, (_, i) => <div key={i} className="flex animate-pulse gap-4 py-4"><div className="size-12 rounded-xl bg-muted" /><div className="flex flex-1 flex-col gap-3 pt-1"><div className="h-4 w-1/3 rounded bg-muted" /><div className="h-3 w-3/4 rounded bg-muted" /></div></div>)}</div>}
      {!loading && section(active, 'connectors.connectedSection', 'connectors.connectedDescription')}
      {!loading && section(inactive.slice(0, visibleCount), 'connectors.exploreSection', 'connectors.exploreDescription', inactive.length)}
      {!loading && hasMore && <div ref={moreRef} className="flex justify-center px-3" data-testid="integration-load-more">
        <Button variant="outline" shape="pill" onClick={loadMore}>{t('connectors.loadMore')}</Button>
      </div>}
      {!loading && !unavailable && filtered.length === 0 && <div className="flex flex-col items-center gap-3 px-6 py-16 text-center"><Search className="mb-2 size-7 text-muted-foreground" /><Text>{t(connectors.length ? 'connectors.noResults' : 'connectors.noConnectionsTitle')}</Text><Text size="sm" color="muted">{t(connectors.length ? 'connectors.noResultsDescription' : 'connectors.noConnectionsDescription')}</Text>{connectors.length > 0 && <Button variant="outline" shape="pill" onClick={() => { changeSearch(''); setCategory('') }}>{t('connectors.clearSearch')}</Button>}</div>}
      <IntegrationDetails connector={selected} requestState={requestStates[selectedSlug] ?? 'idle'} busy={busy === selectedSlug} onClose={() => setSelectedSlug('')}
        onCloseAutoFocus={event => {
          if (openerRef.current?.isConnected && !openerRef.current.hasAttribute('disabled')) {
            event.preventDefault()
            openerRef.current.focus()
          }
        }}
        onConnect={() => { if (selected) void connect(selected.slug) }} onDisconnect={() => { if (selected) void disconnect(selected.slug) }} onRequest={() => { if (selected) void request(selected) }} />
    </section>
  )
}
